import LoadingButton from '@mui/lab/LoadingButton';
import { Button, DialogActions, DialogTitle, useTheme } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import { type FC, useState } from 'react';

import useSnackBar from '../hooks/use-snack-bar.js';
import { humanize } from '../utils/index.js';

interface DeleteDialogProps {
  name: string;
  open: boolean;
  onClose: () => void;
  children?: React.ReactNode;
  onSubmit: () => Promise<void>;
  refetch?: () => void;
  trackFn?: () => void;
}

const DeleteDialog: FC<DeleteDialogProps> = ({ name, open, onClose, children, onSubmit, refetch, trackFn }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const theme = useTheme();
  const snackBar = useSnackBar();

  const handleClosing = () => {
    onClose();
    setTimeout(() => setIsSubmitting(false), theme.transitions.duration.leavingScreen);
  };
  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onSubmit();
      snackBar.success(`${humanize(name)} deleted successfully!`);
      if (trackFn) {
        trackFn();
      }
      if (refetch) {
        refetch();
      }
    } catch (e) {
      snackBar.error(`${humanize(name)} deletion failed!`);
    }
    handleClosing();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby={`${name}-alert-dialog-title`}
      aria-describedby={`${name}-alert-dialog-description`}
    >
      <DialogTitle id={`${name}-alert-dialog-title`}>Are you sure you want to delete this {humanize(name)}?</DialogTitle>
      {children}
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <LoadingButton onClick={handleSubmit} autoFocus loading={isSubmitting} variant={'contained'} color={'error'}>
          Delete
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteDialog;
